import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import { toast } from "react-toastify";
import { Group } from "@mui/icons-material";
import StringList from "../../../form/StringList";
import DepartmentUserModal from "../../DepartmentUserModal";

export default function SettingEditForm() {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const initialSnapshotRef = useRef(null);
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

  const [userModalOpen, setUserModalOpen] = useState(false);
  const [selectedDept, setSelectedDept] = useState(null);

  // stringやobjectを{id, name}に正規化
  const normalizeAny = (d) => {
    if (typeof d === "string") return { id: null, name: d.trim() };
    if (d && typeof d === "object") {
      return {
        id: d.id ?? null,
        name: (d.name ?? d.value ?? "").toString().trim(),
      };
    }
    return { id: null, name: "" };
  };

  // 配列を正規化、stripEmptyなら空行を除外
  const normalizeList = (rows, { stripEmpty = false } = {}) => {
    const list = (Array.isArray(rows) ? rows : []).map(normalizeAny);
    return stripEmpty ? list.filter((x) => x.name !== "") : list;
  };

  // 差分比較前に順序を固定
  const sortForCompare = (arr) =>
    [...arr].sort(
      (a, b) =>
        (a.id ?? Number.POSITIVE_INFINITY) -
          (b.id ?? Number.POSITIVE_INFINITY) || a.name.localeCompare(b.name)
    );

  // 配列の完全一致判定
  const arraysEqual = (a, b) => {
    if (a.length !== b.length) return false;
    return a.every((x, i) => x.id === b[i].id && x.name === b[i].name);
  };

  // 差分判定用
  const takeSnapshot = (rows) =>
    normalizeList(rows, { stripEmpty: true }).map(({ id, name }) => ({
      id,
      name,
    }));

  // 設定をサーバから取得
  const fetchSettings = async () => {
    const res = await fetch("/company/settings.json", {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
    if (!res.ok) throw new Error("failed");
    const data = await res.json();
    const raw = Array.isArray(data?.departments) ? data.departments : [];
    const normalized = raw.map(normalizeAny);
    setDepartments(normalized);
    initialSnapshotRef.current = takeSnapshot(normalized);
  };

  // 初期ロード
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        await fetchSettings();
      } catch {
        if (alive) toast.error("設定の取得に失敗しました");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // 重複名を検出
  const duplicateNames = useMemo(() => {
    const names = normalizeList(departments, { stripEmpty: true }).map(
      (d) => d.name
    );
    return [...new Set(names.filter((n, i) => names.indexOf(n) !== i))];
  }, [departments]);

  // 差分判定
  const hasChanges = useMemo(() => {
    const snapshot = initialSnapshotRef.current;
    if (!snapshot) return false;
    const nowNorm = sortForCompare(takeSnapshot(departments));
    const snapNorm = sortForCompare(snapshot);
    return !arraysEqual(nowNorm, snapNorm);
  }, [departments]);

  // StringListの変更を受け取り、同名ならidを復元
  const handleListChange = (next) => {
    const prevByName = new Map(
      normalizeList(departments, { stripEmpty: true }).map((d) => [
        d.name,
        d.id,
      ])
    );
    const reconciled = normalizeList(next).map((d) => {
      if (d.id == null && d.name && prevByName.has(d.name)) {
        return { ...d, id: prevByName.get(d.name) };
      }
      return d;
    });
    setDepartments(reconciled);
  };

  // 保存処理
  const handleSubmit = async (e) => {
    e.preventDefault();
    const active = normalizeList(departments, { stripEmpty: true });

    if (duplicateNames.length > 0) {
      toast.error(`部署名が重複しています: ${duplicateNames.join(", ")}`);
      return;
    }
    if (!hasChanges) {
      toast.info("変更はありません");
      return;
    }

    // 名前からidを復元
    const nameToId = new Map(
      (initialSnapshotRef.current ?? [])
        .filter((d) => d.id != null)
        .map((d) => [d.name, d.id])
    );
    const recovered = active.map((d) =>
      d.id != null
        ? d
        : nameToId.has(d.name)
        ? { ...d, id: nameToId.get(d.name) }
        : d
    );

    const attrs = recovered.map((d) =>
      d.id != null ? { id: d.id, name: d.name } : { name: d.name }
    );

    // 削除検出
    const initialIds = new Set(
      (initialSnapshotRef.current ?? []).map((d) => d.id).filter(Boolean)
    );
    const currentIds = new Set(
      recovered.filter((d) => d.id != null).map((d) => d.id)
    );
    initialIds.forEach((id) => {
      if (!currentIds.has(id)) attrs.push({ id, _destroy: true });
    });

    setSaving(true);
    try {
      const res = await fetch("/company/settings", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
        },
        credentials: "same-origin",
        body: JSON.stringify({ company: { departments_attributes: attrs } }),
      });

      if (!res.ok) {
        let msg = "更新に失敗しました";
        try {
          const data = await res.json();
          if (data?.errors) {
            msg = Array.isArray(data.errors)
              ? data.errors.join("\n")
              : String(data.errors);
          }
        } catch {}
        toast.error(msg);
        return;
      }

      toast.success("更新しました");
      await fetchSettings();
    } catch {
      toast.error("更新に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  // モーダルを開く
  const openDeptUsers = (dept) => {
    if (!dept?.id) {
      toast.info("未保存の部署です。保存後にユーザーを確認できます。");
      return;
    }
    setSelectedDept(dept);
    setUserModalOpen(true);
  };

  const closeDeptUsers = () => {
    setUserModalOpen(false);
    setSelectedDept(null);
  };

  if (loading) {
    return (
      <Box p={4} ml={4}>
        <Typography>読み込み中...</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ position: "relative", mt: 2, mb: 10 }}>
      <form onSubmit={handleSubmit}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">
            設定
          </Typography>
        </Box>

        <Grid container spacing={1}>
          <Grid item xs={12} sx={{ mb: 2 }}>
            <StringList
              values={departments}
              onChange={handleListChange}
              isRequired={false}
              label="部署"
              unique
              renderActions={(row) => (
                <Tooltip
                  title={
                    row?.id
                      ? "所属メンバーが確認できます"
                      : "保存後に確認できます"
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      aria-label="所属ユーザーが確認できます"
                      onClick={() => openDeptUsers(row)}
                      disabled={!row?.id}
                      sx={{
                        bgcolor: row?.id ? "primary.main" : "grey.300", // idなしはグレー背景
                        color: row?.id ? "white" : "grey.600",
                        "&:hover": {
                          bgcolor: row?.id ? "primary.dark" : "grey.300",
                        },
                      }}
                    >
                      <Group fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            />
          </Grid>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.5, display: "block" }}
          >
            追加/削除後は「更新」を押してください
          </Typography>
        </Grid>

        <Box
          sx={{
            position: "absolute",
            bottom: -60,
            right: 16,
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
          }}
        >
          <Button
            type="submit"
            variant="contained"
            color="inherit"
            sx={{
              color: "#fff",
              bgcolor: "#000",
              "&:hover": { bgcolor: "#333" },
            }}
          >
            更新
          </Button>
        </Box>
      </form>

      {/* 所属部署のユーザー確認モーダル */}
      <DepartmentUserModal
        open={userModalOpen}
        onClose={closeDeptUsers}
        deptId={selectedDept?.id}
        deptName={selectedDept?.name}
      />
    </Container>
  );
}
